import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { jobs, companyName, fetchPublicJobs, fetchInviteJob, type Job } from "@/components/site/jobs";
import { Nav } from "@/components/hirekit/Nav";
import { trackInviteApi } from "@/components/site/admin";
import { PasswordInput } from "@/components/site/PasswordInput";
import {
  applyToJob,
  candidateLogin,
  fetchMyApplications,
  fetchMyProfile,
  fetchMyShowcase,
  getCandidateSession,
} from "@/lib/candidate-api";
import { applyRequirements, type Requirement } from "@/components/site/readiness";


export const Route = createFileRoute("/apply/$jobId")({
  validateSearch: (search: Record<string, unknown>): { invite: string; inv?: string } => ({
    invite: typeof search["invite"] === "string" ? (search["invite"] as string) : "",
    ...(typeof search["inv"] === "string" && search["inv"] ? { inv: search["inv"] as string } : {}),
  }),
  loader: ({ params }) => {
    const job = jobs.find((j) => j.id === params.jobId);
    return { job: job ?? null, jobId: params.jobId };
  },
  head: ({ loaderData }) => {
    if (!loaderData?.job) {
      return { meta: [{ title: "Role not found | CLOSDD" }, { name: "robots", content: "noindex" }] };
    }
    const t = `Apply to ${loaderData.job.title} at ${companyName(loaderData.job)} | CLOSDD`;
    const d = `Log in or create your CLOSDD profile to apply for ${loaderData.job.title}. Upload your CV and a 60-second pitch.`;
    return {
      meta: [
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: ApplyGate,
});

const inputClass =
  "w-full rounded-full border border-ink/15 bg-white px-5 py-3.5 text-[16px] text-ink outline-none focus:border-purple-strong";

function ApplyGate() {
  const { job: staticJob, jobId } = Route.useLoaderData();
  const { invite: inviteEmail, inv = "" } = Route.useSearch();
  const invite = inviteEmail || inv;
  const [job, setJob] = useState<Job | null>(staticJob);
  // A real (API-backed) job is never in the static seed list the route
  // loader checks, so `job` starts null for it and stays null for the
  // whole round trip below — without tracking that separately from "not
  // found", the "This role is not available" screen flashed on every
  // single visit before quietly correcting itself once the fetch resolved.
  const [checkingJob, setCheckingJob] = useState(!staticJob);

  useEffect(() => {
    if (inv) void trackInviteApi(inv, "Application started");
  }, [inv]);

  useEffect(() => {
    if (staticJob) return;
    // A role that is paused, closed, rejected, or still pending approval
    // normally can't be applied for — GET /api/public/jobs enforces that.
    // A valid ?inv= invite token is its own authorization to apply for
    // this one specific job though, regardless of its public-listing state.
    let cancelled = false;
    fetchPublicJobs()
      .then(async (live) => {
        const match = live.find((j) => j.id === jobId);
        if (match) {
          if (!cancelled) setJob(match);
          return;
        }
        if (inv) {
          const invited = await fetchInviteJob(inv);
          if (!cancelled && invited) setJob(invited);
        }
      })
      .catch(() => {
        /* leaves job null — the "not available" screen covers this */
      })
      .finally(() => {
        if (!cancelled) setCheckingJob(false);
      });
    return () => {
      cancelled = true;
    };
  }, [staticJob, jobId, inv]);

  // Signed in candidates never see the login wall: the application is sent
  // straight through from their CLOSDD profile.
  const [sent, setSent] = useState<"pending" | "sent" | "already" | "guest">("pending");
  const [blocked, setBlocked] = useState<Requirement[] | null>(null);
  const [applyError, setApplyError] = useState("");
  const [email, setEmail] = useState(invite);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  async function checkAndApply(current: Job) {
    setApplyError("");
    try {
      const mine = await fetchMyApplications();
      const already = mine.some((a) => a.jobId === current.id);

      // The application record itself is what makes a candidate show up
      // against this job in admin — that used to wait until the profile
      // was fully complete (CV, pitch, assessment), so an invited
      // candidate who'd only just signed up never appeared there at all.
      // Create it as soon as they're signed in and land on this job; the
      // "finish your profile" nudge below is now purely informational,
      // not a gate on being recorded as an applicant.
      if (!already) {
        await applyToJob(current.id);
        if (inv) void trackInviteApi(inv, "Application completed");
      }

      const [profile, showcase] = await Promise.all([fetchMyProfile(), fetchMyShowcase()]);
      const missing = applyRequirements(profile, showcase).filter((r) => !r.ok);
      if (missing.length > 0) {
        setBlocked(missing);
        return;
      }
      setSent(already ? "already" : "sent");
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : "We could not send your application. Try again.");
    }
  }

  useEffect(() => {
    if (!job) return;
    let cancelled = false;
    (async () => {
      const session = await getCandidateSession();
      if (cancelled) return;
      if (!session) {
        setSent("guest");
        return;
      }
      await checkAndApply(job);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, inv]);

  if (job && (sent === "sent" || sent === "already")) {
    return <Sent job={job} already={sent === "already"} />;
  }

  if (job && blocked) {
    return <Incomplete job={job} missing={blocked} />;
  }

  if (!job && checkingJob) return <div className="cl min-h-screen bg-cream" />;

  if (!job) {
    return (
      <div className="cl min-h-screen bg-cream">
        <Nav />
        <main className="mx-auto w-full max-w-[720px] px-5 py-24 text-center">
          <h1 className="font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink">This role is not available.</h1>
          <p className="mt-4 text-[17px] text-ink/65">
            The link may have expired. Check the invitation email or browse the live roles.
          </p>
          <Link
            to="/find-jobs"
            className="mt-8 inline-flex rounded-full bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white"
          >
            Find jobs
          </Link>
        </main>
      </div>
    );
  }

  if (sent === "pending") {
    return (
      <div className="cl min-h-screen bg-cream">
        <Nav />
        <main className="mx-auto w-full max-w-[720px] px-5 py-24 text-center">
          {applyError ? (
            <>
              <p className="text-[17px] text-ink/70">{applyError}</p>
              <button
                type="button"
                onClick={() => void checkAndApply(job)}
                className="mt-6 inline-flex rounded-full bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition hover:-translate-y-0.5"
              >
                Try again
              </button>
            </>
          ) : (
            <p className="text-[13px] font-bold uppercase tracking-[0.2em] text-purple-strong">Checking your account…</p>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="cl min-h-screen bg-cream">
      <Nav />
      <main className="mx-auto w-full max-w-[1020px] px-5 pb-20 pt-8">
        {invite ? (
          <div className="mb-7 rounded-[20px] border border-purple-strong/20 bg-purple-soft px-6 py-5">
            <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-purple-strong">Invitation only</p>
            <p className="mt-2 text-[17px] leading-[1.6] text-ink/75">
              You have been invited to apply directly for {job.title}. This role is not advertised publicly. Create your
              free account or log in and your application goes straight to the hiring manager.
            </p>
          </div>
        ) : null}
        <Link
          to="/jobs/$jobId"
          params={{ jobId: job.id }}
          className="text-[12px] font-bold uppercase tracking-[0.24em] text-purple-strong"
        >
          ← Back to advert
        </Link>

        <h1 className="mt-5 max-w-[760px] font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink sm:text-[32px]">
          {invite ? `You have been invited to apply for ${job.title}` : `Log in to apply for ${job.title}`}
        </h1>
        <p className="mt-3 text-[13px] uppercase tracking-[0.14em] text-ink/50">
          {companyName(job)} · {job.location} · {job.setting} · {job.contract}
        </p>

        <div className="mt-10 grid gap-5 lg:grid-cols-2">
          <form
            className="flex flex-col rounded-[28px] border border-ink/10 bg-white/75 p-7 lg:p-9"
            onSubmit={async (e) => {
              e.preventDefault();
              setLoginError("");
              try {
                await candidateLogin(email.trim().toLowerCase(), password.trim());
              } catch (err) {
                setLoginError(err instanceof Error ? err.message : "We could not match those candidate details.");
                return;
              }
              await checkAndApply(job);
            }}
          >
            <div className="grid flex-1 gap-5">
              <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-purple-strong">Log in</p>
              <label className="grid gap-2">
                <span className="text-[12px] font-bold uppercase tracking-[0.18em] text-ink/50">Email</span>
                <input
                  required
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className={inputClass}
                />
              </label>
              <label className="grid gap-2">
                <span className="text-[12px] font-bold uppercase tracking-[0.18em] text-ink/50">Password</span>
                <PasswordInput
                  required
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`${inputClass} pr-12`}
                />
              </label>
              {loginError ? <p className="text-[14px] leading-[1.5] text-red-600">{loginError}</p> : null}
              {applyError ? <p className="text-[14px] leading-[1.5] text-red-600">{applyError}</p> : null}
              <Link
                to="/forgot-password"
                className="self-start mb-6 text-[13px] font-medium text-purple-strong underline underline-offset-4 hover:text-ink"
              >
                Forgot your password?
              </Link>
            </div>
            <button
              type="submit"
              className="mt-auto justify-self-start rounded-full bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition hover:-translate-y-0.5"
            >
              Log in and apply
            </button>
          </form>


          <div className="flex flex-col rounded-[28px] border border-purple-strong/15 bg-purple-soft p-7 lg:p-9">
            <div className="flex-1">
              <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-purple-strong">New here?</p>
              <h2 className="mt-3 font-serif text-[22px] leading-[1.08] tracking-[-0.02em] text-ink">
                Create an account to pitch for this role.
              </h2>
              <p className="mt-4 text-[17px] leading-[1.65] text-ink/65">
                Free to create your profile. Upload your CV and record a 60-second pitch once, then apply to any sales role.
              </p>
            </div>
            <Link
              to="/signup"
              search={{ jobId: job.id, invite, ...(inv ? { inv } : {}) }}
              className="mt-auto inline-flex self-start rounded-full bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition hover:-translate-y-0.5"
            >
              Create an account
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

/** Confirmation shown when a signed in candidate's application goes through. */
function Sent({ job, already }: { job: Job; already: boolean }) {
  return (
    <div className="cl min-h-screen bg-cream">
      <Nav />
      <main className="mx-auto w-full max-w-[820px] px-5 pb-24 pt-12">
        <div className="rounded-[30px] border border-purple-strong/15 bg-white/80 p-8 lg:p-12">
          <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-purple-strong">
            {already ? "Already applied" : "Application sent"}
          </p>
          <h1 className="mt-4 font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink sm:text-[32px]">
            {already
              ? `You've already applied for ${job.title}.`
              : "Thank you for applying."}
          </h1>
          <p className="mt-5 max-w-[620px] text-[16px] leading-[1.65] text-ink/70">
            {already
              ? `Your CLOSDD profile and 60-second pitch are already with ${companyName(job)}. They'll be in touch if you're a fit.`
              : `Your application has been sent to ${companyName(job)} — your CLOSDD profile, your CV and your 60-second pitch went with it.`}
          </p>
          <p className="mt-3 text-[13px] uppercase tracking-[0.14em] text-ink/50">
            {job.title} · {companyName(job)} · {job.location}
          </p>

          <div className="mt-9 grid gap-3 sm:flex">
            <Link
              to="/find-jobs"
              className="inline-flex justify-center rounded-full bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition hover:-translate-y-0.5"
            >
              Apply for more roles
            </Link>
            <Link
              to="/dashboard"
              className="inline-flex justify-center rounded-full border border-purple-strong/30 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-purple-strong transition hover:bg-purple-soft/60"
            >
              Back to dashboard
            </Link>
          </div>

          <div className="mt-10 border-t border-ink/10 pt-7">
            <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-ink/45">What happens next</p>
            <ol className="mt-4 grid gap-3 text-[16px] leading-[1.6] text-ink/70">
              <li>01 — The employer listens to your 60-second pitch.</li>
              <li>02 — Strong pitches get shortlisted and contacted directly.</li>
              <li>03 — Keep your pitch and CV fresh so every application lands well.</li>
            </ol>
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * Shown when a signed in candidate hasn't finished their CLOSDD profile.
 * Employers only ever receive complete profiles, so the application is held here.
 */
function Incomplete({ job, missing }: { job: Job; missing: Requirement[] }) {
  return (
    <div className="cl min-h-screen bg-cream">
      <Nav />
      <main className="mx-auto w-full max-w-[820px] px-5 pb-24 pt-12">
        <div className="rounded-[30px] border border-purple-strong/15 bg-white/80 p-8 lg:p-12">
          <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-purple-strong">
            Finish your profile to apply
          </p>
          <h1 className="mt-4 font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink sm:text-[32px]">
            Your profile isn't ready to send yet.
          </h1>
          <p className="mt-5 max-w-[620px] text-[16px] leading-[1.65] text-ink/70">
            CLOSDD only sends complete profiles to employers — your CV, your 60-second pitch and your recorded sales
            answers. Finish the {missing.length === 1 ? "step" : `${missing.length} steps`} below and you can apply to{" "}
            {job.title} in one click.
          </p>

          <ul className="mt-9 grid gap-3">
            {missing.map((r) => (
              <li key={r.key} className="rounded-[20px] border border-ink/10 bg-cream/70 px-6 py-5">
                <p className="text-[16px] font-semibold text-ink">{r.label}</p>
                <p className="mt-1 text-[15px] leading-[1.6] text-ink/60">{r.detail}</p>
              </li>
            ))}
          </ul>

          <div className="mt-9 grid gap-3 sm:flex">
            <Link
              to="/dashboard"
              className="inline-flex justify-center rounded-full bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition hover:-translate-y-0.5"
            >
              Complete my profile
            </Link>
            <Link
              to="/jobs/$jobId"
              params={{ jobId: job.id }}
              className="inline-flex justify-center rounded-full border border-purple-strong/30 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-purple-strong transition hover:bg-purple-soft/60"
            >
              Back to advert
            </Link>
          </div>

          <p className="mt-8 text-[14px] leading-[1.6] text-ink/50">
            Nothing is lost — once your profile is complete, come back to this role and your application sends instantly.
          </p>
        </div>
      </main>
    </div>
  );
}
